// pages/UpgradeTiers.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { load as loadCashfree } from '@cashfreepayments/cashfree-js';
import {
  FiArrowLeft,
  FiCheck,
  FiX,
  FiInfo,
  FiLoader,
  FiAlertCircle,
  FiFileText,
  FiCamera,
  FiClock,
  FiHardDrive
} from 'react-icons/fi';
import DashboardLayout from '../components/DashboardLayout';
import { useAuth } from '../services/auth';
import supabase from '../services/supabase';
import {
  createEventTierOrder,
  verifyEventTierPayment,
  calculateGST,
  formatCurrency,
  checkTierPurchased
} from '../services/eventUpgradeService';
import {
  getIndianStates,
  getStateCode,
  PLATFORM_DETAILS
} from '../services/eventUpgradeInvoiceService';
import {
  getEventLinkedAlbums,
  getAlbumExtensionPricing,
  calculateExtensionPrice,
  createAlbumExtensionOrder,
  verifyAlbumExtensionPayment,
  formatStorageSize,
  formatDate as formatAlbumDate,
  getExpiryStatus
} from '../services/albumExtensionService';
import {
  getEventPhotoUploadStatus,
  getPhotoUploadExtensionPricing,
  createPhotoUploadExtensionOrder,
  verifyPhotoUploadExtensionPayment,
  getExpiryStatusBadge,
  formatDate as formatPhotoDate,
  calculateGST as calculatePhotoGST
} from '../services/photoUploadExtensionService';
import '../styles/UpgradeTiers.scss';

// Cashfree environment
const CASHFREE_ENV = process.env.REACT_APP_CASHFREE_ENV || 'sandbox';

// Debug: Log the environment on module load
console.log('💳 Cashfree Environment configured:', CASHFREE_ENV);
if (CASHFREE_ENV === 'sandbox') {
  console.warn('⚠️ Cashfree is in SANDBOX mode. If your backend is in production, payments will fail!');
}

const TierCard = ({ tier, isCurrent, tierType, onUpgrade }) => {
  const isFree = tier.price === 0;
  
  // Host tier features
  const hostFeatures = tierType === 'host' ? [
    {
      text: tier.notification_blasts === -1 
        ? 'Unlimited Notification Blasts' 
        : `${tier.notification_blasts} Notification Blast${tier.notification_blasts > 1 ? 's' : ''}`,
      included: true,
    },
    {
      text: tier.max_attendees === -1 
        ? 'Unlimited Guests' 
        : `Max ${tier.max_attendees} Guests`,
      included: true,
    },
    {
      text: 'Custom RSVP Emoji',
      included: tier.custom_rsvp_emoji,
    },
    {
      text: 'Download Guest List',
      included: tier.guestlist_download,
    },
    {
      text: 'Download RSVP List',
      included: tier.attendee_list_download,
    },
    {
      text: 'Passes Sales Report',
      included: tier.sales_report,
    },
  ] : [];

  // Photo tier features
  const photoFeatures = tierType === 'photo' ? [
    {
      text: `${tier.photo_upload_limit} Photos per Event`,
      included: true,
    },
    {
      text: `Photo Access: ${tier.photo_access_duration}`,
      included: true,
    },
    {
      text: `Upload Photos Within: ${tier.upload_duration}`,
      included: true,
    },
    {
      text: tier.id === 'photo_free'
        ? 'Photos stored forever'
        : 'Valid for 1 year (can be extended)',
      included: true,
    },
  ] : [];

  const features = tierType === 'host' ? hostFeatures : photoFeatures;

  return (
    <motion.div
      className={`tier-card ${isCurrent ? 'current' : ''}`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -4 }}
    >
      {isCurrent && (
        <div className="current-badge">
          <span>Current Plan</span>
        </div>
      )}

      <div className="tier-header">
        <h3 className="tier-name">{tier.display_name}</h3>
        {tier.price > 0 ? (
          <div className="price-container">
            <span className="price">₹{tier.price}</span>
            <span className="tax-note">+ 18% GST</span>
            {tierType === 'photo' && <span className="validity-note">/ year</span>}
          </div>
        ) : (
          <div className="price-container">
            <span className="free-text">Free</span>
            {tierType === 'photo' && <span className="validity-note">Lifetime</span>}
          </div>
        )}
      </div>

      <div className="features-list">
        {features.map((feature, idx) => (
          <div key={idx} className={`feature ${feature.included ? 'included' : 'excluded'}`}>
            {feature.included ? (
              <FiCheck className="icon" />
            ) : (
              <FiX className="icon" />
            )}
            <span>{feature.text}</span>
          </div>
        ))}
      </div>

      <div className="tier-footer">
        {isCurrent ? (
          <div className="current-status">
            <FiCheck size={20} />
            <span>Active Plan</span>
          </div>
        ) : isFree ? (
          <div className="free-status">
            <span>Free Tier</span>
          </div>
        ) : (
          <button 
            className="upgrade-btn"
            onClick={() => onUpgrade(tier)}
          >
            Upgrade to {tier.display_name}
          </button>
        )}
      </div>
    </motion.div>
  );
};

// Payment Modal Component
const PaymentModal = ({
  isOpen,
  onClose,
  tier,
  tierType,
  eventId,
  eventName,
  user,
  onPaymentSuccess
}) => {
  const [step, setStep] = useState('details'); // details, processing, success, error
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState(user?.email || '');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerState, setCustomerState] = useState('');
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [paymentResult, setPaymentResult] = useState(null);
  const [loadingUserDetails, setLoadingUserDetails] = useState(false);

  const pricing = tier ? calculateGST(tier.price) : null;
  const indianStates = getIndianStates();

  // Fetch user details from users table when modal opens
  useEffect(() => {
    const fetchUserDetails = async () => {
      if (!isOpen || !user?.id) return;

      setLoadingUserDetails(true);
      try {
        const { data, error: fetchError } = await supabase
          .from('users')
          .select('first_name, last_name, phone_number, email, state')
          .eq('id', user.id)
          .single();

        if (!fetchError && data) {
          // Build full name from first_name and last_name
          const fullName = [data.first_name, data.last_name].filter(Boolean).join(' ') || '';

          setCustomerName(fullName);
          setCustomerEmail(data.email || user?.email || '');

          // Clean phone number (remove country code if present)
          if (data.phone_number) {
            let phone = data.phone_number.replace(/[\s\-\(\)]/g, '');
            if (phone.startsWith('+91')) {
              phone = phone.substring(3);
            } else if (phone.startsWith('91') && phone.length > 10) {
              phone = phone.substring(2);
            }
            setCustomerPhone(phone);
          }

          if (data.state) {
            setCustomerState(data.state);
          }
        } else if (fetchError) {
          console.error('Error fetching user details:', fetchError);
        }
      } catch (err) {
        console.error('Error fetching user details:', err);
      } finally {
        setLoadingUserDetails(false);
      }
    };

    fetchUserDetails();
  }, [isOpen, user?.id]);

  const handlePayment = async () => {
    if (!customerName || !customerEmail || !customerPhone) {
      setError('Please fill in all required fields');
      return;
    }

    // Validate phone number
    if (!/^[6-9]\d{9}$/.test(customerPhone)) {
      setError('Please enter a valid 10-digit phone number');
      return;
    }

    // Validate email
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerEmail)) {
      setError('Please enter a valid email address');
      return;
    }

    setProcessing(true);
    setError('');
    setStep('processing');

    try {
      // Check if already purchased
      const purchaseCheck = await checkTierPurchased(eventId, tier.id);
      if (purchaseCheck.purchased) {
        setError('You have already purchased this tier for this event');
        setStep('error');
        setProcessing(false);
        return;
      }

      // Create order
      const orderResult = await createEventTierOrder({
        eventId,
        tierId: tier.id,
        tierType,
        baseAmount: tier.price,
        customerName,
        customerEmail,
        customerPhone,
        customerState: customerState || null,
      });

      if (!orderResult.success) {
        throw new Error(orderResult.error || 'Failed to create order');
      }

      console.log('📦 Order created:', orderResult.orderId);
      console.log('🔑 Payment Session ID:', orderResult.paymentSessionId);
      console.log('🌍 Cashfree Environment:', CASHFREE_ENV);

      // Validate payment session ID
      if (!orderResult.paymentSessionId) {
        throw new Error('Payment session ID not received from server');
      }

      // Initialize Cashfree
      const cashfree = await loadCashfree({
        mode: CASHFREE_ENV,
      });

      console.log('✅ Cashfree SDK loaded');

      // Open payment checkout
      const checkoutOptions = {
        paymentSessionId: orderResult.paymentSessionId,
        redirectTarget: '_modal',
      };

      console.log('🚀 Opening checkout with options:', checkoutOptions);

      cashfree.checkout(checkoutOptions).then(async (result) => {
        if (result.error) {
          console.error('Payment error:', result.error);
          setError(result.error.message || 'Payment failed');
          setStep('error');
          setProcessing(false);
          return;
        }

        if (result.redirect) {
          // Payment redirected - will be handled on return
          console.log('Payment redirected');
          return;
        }

        if (result.paymentDetails) {
          // Verify payment on server
          const verifyResult = await verifyEventTierPayment(orderResult.orderId);

          if (verifyResult.success && verifyResult.paymentStatus === 'completed') {
            setPaymentResult({
              invoiceNumber: verifyResult.invoiceNumber,
              invoiceId: verifyResult.invoiceId,
              tierName: tier.display_name,
              amount: pricing.total,
            });
            setStep('success');
            onPaymentSuccess && onPaymentSuccess(verifyResult);
          } else {
            setError(verifyResult.error || 'Payment verification failed');
            setStep('error');
          }
        }
      }).catch((err) => {
        console.error('Checkout error:', err);
        setError(err.message || 'Payment checkout failed');
        setStep('error');
      }).finally(() => {
        setProcessing(false);
      });

    } catch (err) {
      console.error('Payment error:', err);
      setError(err.message || 'An error occurred');
      setStep('error');
      setProcessing(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="payment-modal-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="payment-modal"
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          onClick={(e) => e.stopPropagation()}
        >
          {step === 'details' && (
            <>
              <div className="modal-header">
                <h2>Upgrade to {tier?.display_name}</h2>
                <p className="event-name">{eventName}</p>
              </div>

              <div className="modal-content">
                {/* Pricing Summary */}
                <div className="pricing-summary">
                  <div className="price-row">
                    <span>Base Price</span>
                    <span>{formatCurrency(pricing?.base)}</span>
                  </div>
                  <div className="price-row">
                    <span>GST (18%)</span>
                    <span>{formatCurrency(pricing?.gst)}</span>
                  </div>
                  <div className="price-row total">
                    <span>Total</span>
                    <span>{formatCurrency(pricing?.total)}</span>
                  </div>
                </div>

                {/* Customer Details Form */}
                <div className="customer-form">
                  <h3>Billing Details</h3>

                  <div className="form-group">
                    <label>Full Name *</label>
                    <input
                      type="text"
                      value={customerName}
                      onChange={(e) => setCustomerName(e.target.value)}
                      placeholder="Enter your full name"
                    />
                  </div>

                  <div className="form-group">
                    <label>Email *</label>
                    <input
                      type="email"
                      value={customerEmail}
                      onChange={(e) => setCustomerEmail(e.target.value)}
                      placeholder="Enter your email"
                    />
                  </div>

                  <div className="form-group">
                    <label>Phone *</label>
                    <input
                      type="tel"
                      value={customerPhone}
                      onChange={(e) => setCustomerPhone(e.target.value)}
                      placeholder="10-digit phone number"
                      maxLength={10}
                    />
                  </div>

                  <div className="form-group">
                    <label>State (for GST)</label>
                    <select
                      value={customerState}
                      onChange={(e) => setCustomerState(e.target.value)}
                    >
                      <option value="">Select State</option>
                      {indianStates.map((state) => (
                        <option key={state} value={state}>
                          {state}
                        </option>
                      ))}
                    </select>
                    <small className="help-text">
                      Select your state for proper GST calculation
                    </small>
                  </div>
                </div>

                {error && (
                  <div className="error-message">
                    <FiAlertCircle />
                    <span>{error}</span>
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button className="btn-cancel" onClick={onClose}>
                  Cancel
                </button>
                <button
                  className="btn-pay"
                  onClick={handlePayment}
                  disabled={processing}
                >
                  Pay {formatCurrency(pricing?.total)}
                </button>
              </div>
            </>
          )}

          {step === 'processing' && (
            <div className="modal-processing">
              <FiLoader className="spinner" size={48} />
              <h3>Processing Payment</h3>
              <p>Please complete the payment in the popup window...</p>
            </div>
          )}

          {step === 'success' && (
            <div className="modal-success">
              <div className="success-icon">
                <FiCheck size={48} />
              </div>
              <h3>Payment Successful!</h3>
              <p>Your event has been upgraded to {paymentResult?.tierName}</p>

              {paymentResult?.invoiceNumber && (
                <div className="invoice-info">
                  <FiFileText />
                  <span>Invoice: {paymentResult.invoiceNumber}</span>
                </div>
              )}

              <div className="modal-footer">
                <button
                  className="btn-primary"
                  onClick={() => {
                    onClose();
                    window.location.reload();
                  }}
                >
                  Done
                </button>
              </div>
            </div>
          )}

          {step === 'error' && (
            <div className="modal-error">
              <div className="error-icon">
                <FiAlertCircle size={48} />
              </div>
              <h3>Payment Failed</h3>
              <p>{error || 'An error occurred during payment'}</p>

              <div className="modal-footer">
                <button className="btn-cancel" onClick={onClose}>
                  Close
                </button>
                <button
                  className="btn-primary"
                  onClick={() => {
                    setStep('details');
                    setError('');
                  }}
                >
                  Try Again
                </button>
              </div>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// Album Card Component for Photographer Albums tab
const AlbumCard = ({ album, onExtend }) => {
  const expiryStatus = getExpiryStatus(album.storage_expires_at);

  const getStatusColor = () => {
    switch (expiryStatus.status) {
      case 'expired': return 'status-expired';
      case 'critical': return 'status-critical';
      case 'warning': return 'status-warning';
      case 'soon': return 'status-soon';
      case 'active': return 'status-active';
      default: return 'status-default';
    }
  };

  return (
    <motion.div
      className="album-card"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -4 }}
    >
      {/* Album Cover */}
      <div className="album-cover">
        {album.cover_image_url ? (
          <img src={album.cover_image_url} alt={album.name} />
        ) : (
          <div className="placeholder-cover">
            <FiCamera size={32} />
          </div>
        )}
        <div className={`expiry-badge ${getStatusColor()}`}>
          {expiryStatus.label}
        </div>
      </div>

      {/* Album Info */}
      <div className="album-info">
        <h3 className="album-name">{album.name}</h3>

        <div className="album-stats">
          <div className="stat">
            <FiCamera className="icon" />
            <span>{album.photos_count || 0} photos</span>
          </div>
          <div className="stat">
            <FiHardDrive className="icon" />
            <span>{formatStorageSize(album.upload_size_bytes)}</span>
          </div>
          <div className="stat">
            <FiClock className="icon" />
            <span>{formatAlbumDate(album.storage_expires_at)}</span>
          </div>
        </div>

        {album.creator && (
          <div className="photographer-info">
            <span>By: {album.creator.raw_user_meta_data?.first_name || album.creator.email}</span>
          </div>
        )}
      </div>

      {/* Extend Button */}
      <div className="album-footer">
        <button
          className="extend-btn"
          onClick={() => onExtend(album)}
        >
          Extend Storage
        </button>
      </div>
    </motion.div>
  );
};

// Album Extension Modal Component
const AlbumExtensionModal = ({
  isOpen,
  onClose,
  album,
  eventId,
  eventName,
  user,
  pricingTiers,
  onPaymentSuccess
}) => {
  const [step, setStep] = useState('details');
  const [selectedDuration, setSelectedDuration] = useState(null);
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState(user?.email || '');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerState, setCustomerState] = useState('');
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [paymentResult, setPaymentResult] = useState(null);

  const indianStates = getIndianStates();

  // Set default duration when modal opens
  useEffect(() => {
    if (isOpen && pricingTiers.length > 0 && !selectedDuration) {
      setSelectedDuration(pricingTiers[0]);
    }
  }, [isOpen, pricingTiers]);

  // Fetch user details when modal opens
  useEffect(() => {
    const fetchUserDetails = async () => {
      if (!isOpen || !user?.id) return;

      try {
        const { data, error: fetchError } = await supabase
          .from('users')
          .select('first_name, last_name, phone_number, email, state')
          .eq('id', user.id)
          .single();

        if (!fetchError && data) {
          const fullName = [data.first_name, data.last_name].filter(Boolean).join(' ') || '';
          setCustomerName(fullName);
          setCustomerEmail(data.email || user?.email || '');

          if (data.phone_number) {
            let phone = data.phone_number.replace(/[\s\-\(\)]/g, '');
            if (phone.startsWith('+91')) {
              phone = phone.substring(3);
            } else if (phone.startsWith('91') && phone.length > 10) {
              phone = phone.substring(2);
            }
            setCustomerPhone(phone);
          }

          if (data.state) {
            setCustomerState(data.state);
          }
        }
      } catch (err) {
        console.error('Error fetching user details:', err);
      }
    };

    fetchUserDetails();
  }, [isOpen, user?.id]);

  // Calculate price
  const pricing = album && selectedDuration
    ? calculateExtensionPrice(album.upload_size_bytes, selectedDuration)
    : null;

  const handlePayment = async () => {
    if (!customerName || !customerEmail || !customerPhone) {
      setError('Please fill in all required fields');
      return;
    }

    if (!/^[6-9]\d{9}$/.test(customerPhone)) {
      setError('Please enter a valid 10-digit phone number');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerEmail)) {
      setError('Please enter a valid email address');
      return;
    }

    if (!customerState) {
      setError('Please select your state');
      return;
    }

    setProcessing(true);
    setError('');
    setStep('processing');

    try {
      // Create order
      const orderResult = await createAlbumExtensionOrder({
        albumId: album.id,
        durationMonths: selectedDuration.duration_months,
        customerName,
        customerEmail,
        customerPhone,
        customerState,
      });

      if (!orderResult.success) {
        throw new Error(orderResult.error || 'Failed to create order');
      }

      console.log('📦 Album extension order created:', orderResult.orderId);

      // Initialize Cashfree
      const cashfree = await loadCashfree({
        mode: CASHFREE_ENV,
      });

      const checkoutOptions = {
        paymentSessionId: orderResult.paymentSessionId,
        redirectTarget: '_modal',
      };

      cashfree.checkout(checkoutOptions).then(async (result) => {
        if (result.error) {
          console.error('Payment error:', result.error);
          setError(result.error.message || 'Payment failed');
          setStep('error');
          setProcessing(false);
          return;
        }

        if (result.redirect) {
          console.log('Payment redirected');
          return;
        }

        if (result.paymentDetails) {
          const verifyResult = await verifyAlbumExtensionPayment(orderResult.orderId);

          if (verifyResult.success && verifyResult.paymentStatus === 'completed') {
            setPaymentResult({
              invoiceNumber: verifyResult.invoiceNumber,
              albumName: verifyResult.albumName,
              newExpiry: verifyResult.newExpiry,
            });
            setStep('success');
            onPaymentSuccess && onPaymentSuccess(verifyResult);
          } else {
            setError(verifyResult.error || 'Payment verification failed');
            setStep('error');
          }
        }
      }).catch((err) => {
        console.error('Checkout error:', err);
        setError(err.message || 'Payment checkout failed');
        setStep('error');
      }).finally(() => {
        setProcessing(false);
      });

    } catch (err) {
      console.error('Payment error:', err);
      setError(err.message || 'An error occurred');
      setStep('error');
      setProcessing(false);
    }
  };

  const handleClose = () => {
    setStep('details');
    setError('');
    setPaymentResult(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="payment-modal-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={handleClose}
      >
        <motion.div
          className="payment-modal album-extension-modal"
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          onClick={(e) => e.stopPropagation()}
        >
          {step === 'details' && (
            <>
              <div className="modal-header">
                <h2>Extend Album Storage</h2>
                <p className="event-name">{album?.name}</p>
              </div>

              <div className="modal-content">
                {/* Album Info */}
                <div className="album-summary">
                  <div className="summary-row">
                    <span>Album Size</span>
                    <span>{formatStorageSize(album?.upload_size_bytes)}</span>
                  </div>
                  <div className="summary-row">
                    <span>Current Expiry</span>
                    <span>{formatAlbumDate(album?.storage_expires_at)}</span>
                  </div>
                </div>

                {/* Duration Selection */}
                <div className="duration-selection">
                  <h3>Select Duration</h3>
                  <div className="duration-options">
                    {pricingTiers.map((tier) => (
                      <button
                        key={tier.id}
                        className={`duration-btn ${selectedDuration?.id === tier.id ? 'active' : ''}`}
                        onClick={() => setSelectedDuration(tier)}
                      >
                        {tier.display_name}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Pricing Summary */}
                {pricing && (
                  <div className="pricing-summary">
                    <div className="price-row">
                      <span>Album Size</span>
                      <span>{pricing.albumSizeGB} GB</span>
                    </div>
                    <div className="price-row">
                      <span>Price per GB</span>
                      <span>₹{pricing.pricePerGB}</span>
                    </div>
                    <div className="price-row">
                      <span>Duration</span>
                      <span>{pricing.durationMonths} months (x{pricing.durationMultiplier})</span>
                    </div>
                    <div className="price-row">
                      <span>Base Price</span>
                      <span>{formatCurrency(pricing.baseAmount)}</span>
                    </div>
                    <div className="price-row">
                      <span>GST (18%)</span>
                      <span>{formatCurrency(pricing.gstAmount)}</span>
                    </div>
                    <div className="price-row total">
                      <span>Total</span>
                      <span>{formatCurrency(pricing.totalAmount)}</span>
                    </div>
                  </div>
                )}

                {/* Customer Details Form */}
                <div className="customer-form">
                  <h3>Billing Details</h3>

                  <div className="form-group">
                    <label>Full Name *</label>
                    <input
                      type="text"
                      value={customerName}
                      onChange={(e) => setCustomerName(e.target.value)}
                      placeholder="Enter your full name"
                    />
                  </div>

                  <div className="form-group">
                    <label>Email *</label>
                    <input
                      type="email"
                      value={customerEmail}
                      onChange={(e) => setCustomerEmail(e.target.value)}
                      placeholder="Enter your email"
                    />
                  </div>

                  <div className="form-group">
                    <label>Phone *</label>
                    <input
                      type="tel"
                      value={customerPhone}
                      onChange={(e) => setCustomerPhone(e.target.value)}
                      placeholder="10-digit phone number"
                      maxLength={10}
                    />
                  </div>

                  <div className="form-group">
                    <label>State *</label>
                    <select
                      value={customerState}
                      onChange={(e) => setCustomerState(e.target.value)}
                    >
                      <option value="">Select State</option>
                      {indianStates.map((state) => (
                        <option key={state} value={state}>
                          {state}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {error && (
                  <div className="error-message">
                    <FiAlertCircle />
                    <span>{error}</span>
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button className="btn-cancel" onClick={handleClose}>
                  Cancel
                </button>
                <button
                  className="btn-pay"
                  onClick={handlePayment}
                  disabled={processing || !pricing}
                >
                  Pay {pricing ? formatCurrency(pricing.totalAmount) : ''}
                </button>
              </div>
            </>
          )}

          {step === 'processing' && (
            <div className="modal-processing">
              <FiLoader className="spinner" size={48} />
              <h3>Processing Payment</h3>
              <p>Please complete the payment in the popup window...</p>
            </div>
          )}

          {step === 'success' && (
            <div className="modal-success">
              <div className="success-icon">
                <FiCheck size={48} />
              </div>
              <h3>Album Extended!</h3>
              <p>Your album storage has been extended successfully.</p>

              {paymentResult && (
                <div className="success-details">
                  <div className="detail-row">
                    <span>Album</span>
                    <span>{paymentResult.albumName}</span>
                  </div>
                  <div className="detail-row">
                    <span>New Expiry</span>
                    <span>{formatAlbumDate(paymentResult.newExpiry)}</span>
                  </div>
                  {paymentResult.invoiceNumber && (
                    <div className="detail-row">
                      <FiFileText />
                      <span>Invoice: {paymentResult.invoiceNumber}</span>
                    </div>
                  )}
                </div>
              )}

              <div className="modal-footer">
                <button
                  className="btn-primary"
                  onClick={() => {
                    handleClose();
                    window.location.reload();
                  }}
                >
                  Done
                </button>
              </div>
            </div>
          )}

          {step === 'error' && (
            <div className="modal-error">
              <div className="error-icon">
                <FiAlertCircle size={48} />
              </div>
              <h3>Payment Failed</h3>
              <p>{error || 'An error occurred during payment'}</p>

              <div className="modal-footer">
                <button className="btn-cancel" onClick={handleClose}>
                  Close
                </button>
                <button
                  className="btn-primary"
                  onClick={() => {
                    setStep('details');
                    setError('');
                  }}
                >
                  Try Again
                </button>
              </div>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// Photo Upload Extension/Upgrade Modal
const PhotoExtensionModal = ({
  isOpen,
  onClose,
  eventId,
  eventName,
  user,
  extensionType, // 'extension' or 'upgrade'
  selectedTierId,
  photoUploadStatus,
  photoExtensionPricing,
  onPaymentSuccess
}) => {
  const [step, setStep] = useState('details');
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState(user?.email || '');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerState, setCustomerState] = useState('');
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [paymentResult, setPaymentResult] = useState(null);
  const [financialYear, setFinancialYear] = useState(null);

  const indianStates = getIndianStates();

  // Fetch financial year when modal opens
  useEffect(() => {
    const fetchFinancialYear = async () => {
      if (!isOpen || !eventId) return;

      try {
        const { data: event } = await supabase
          .from('events')
          .select('financial_year')
          .eq('id', eventId)
          .single();

        if (event) {
          setFinancialYear(event.financial_year);
        }
      } catch (err) {
        console.error('Error fetching financial year:', err);
      }
    };

    fetchFinancialYear();
  }, [isOpen, eventId]);

  // Fetch user details when modal opens
  useEffect(() => {
    const fetchUserDetails = async () => {
      if (!isOpen || !user?.id) return;

      try {
        const { data, error: fetchError } = await supabase
          .from('users')
          .select('first_name, last_name, phone_number, email, state')
          .eq('id', user.id)
          .single();

        if (!fetchError && data) {
          const fullName = [data.first_name, data.last_name].filter(Boolean).join(' ') || '';
          setCustomerName(fullName);
          setCustomerEmail(data.email || user?.email || '');

          if (data.phone_number) {
            let phone = data.phone_number.replace(/[\s\-\(\)]/g, '');
            if (phone.startsWith('+91')) {
              phone = phone.substring(3);
            } else if (phone.startsWith('91') && phone.length > 10) {
              phone = phone.substring(2);
            }
            setCustomerPhone(phone);
          }

          if (data.state) {
            setCustomerState(data.state);
          }
        }
      } catch (err) {
        console.error('Error fetching user details:', err);
      }
    };

    fetchUserDetails();
  }, [isOpen, user?.id]);

  // Get pricing details
  const tierDetails = photoExtensionPricing?.find(t => t.tier_id === selectedTierId);
  const gstCalc = tierDetails ? calculatePhotoGST(tierDetails.extension_price) : null;

  const handleClose = () => {
    setStep('details');
    setError('');
    setPaymentResult(null);
    onClose();
  };

  const handlePayment = async () => {
    if (!customerName || !customerEmail || !customerPhone) {
      setError('Please fill in all required fields');
      return;
    }

    if (!/^[6-9]\d{9}$/.test(customerPhone)) {
      setError('Please enter a valid 10-digit phone number');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerEmail)) {
      setError('Please enter a valid email address');
      return;
    }

    if (!customerState) {
      setError('Please select your state');
      return;
    }

    if (!financialYear) {
      setError('Financial year not loaded. Please try again.');
      return;
    }

    setProcessing(true);
    setError('');
    setStep('processing');

    try {
      // Create order
      const orderResult = await createPhotoUploadExtensionOrder({
        eventId,
        financialYear,
        purchaseType: extensionType,
        tierId: selectedTierId,
        customerName,
        customerEmail,
        customerPhone,
        customerState,
      });

      if (!orderResult.success) {
        throw new Error(orderResult.error || 'Failed to create order');
      }

      console.log('📦 Photo upload extension order created:', orderResult.orderId);

      // Initialize Cashfree
      const cashfree = await loadCashfree({
        mode: CASHFREE_ENV,
      });

      const checkoutOptions = {
        paymentSessionId: orderResult.paymentSessionId,
        redirectTarget: '_modal',
      };

      cashfree.checkout(checkoutOptions).then(async (result) => {
        if (result.error) {
          console.error('Payment error:', result.error);
          setError(result.error.message || 'Payment failed');
          setStep('error');
          setProcessing(false);
          return;
        }

        if (result.redirect) {
          console.log('Payment redirected');
          return;
        }

        if (result.paymentDetails) {
          const verifyResult = await verifyPhotoUploadExtensionPayment(orderResult.orderId);

          if (verifyResult.success && verifyResult.paymentStatus === 'completed') {
            setPaymentResult({
              invoiceNumber: verifyResult.invoiceNumber,
              tierId: verifyResult.tierId,
              purchaseType: verifyResult.purchaseType,
              newExpiryDate: verifyResult.newExpiryDate,
              photoLimit: verifyResult.photoLimit,
            });
            setStep('success');
            onPaymentSuccess && onPaymentSuccess(verifyResult);
          } else {
            setError(verifyResult.error || 'Payment verification failed');
            setStep('error');
          }
        }

        setProcessing(false);
      });
    } catch (err) {
      console.error('Payment error:', err);
      setError(err.message || 'An error occurred during payment');
      setStep('error');
      setProcessing(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="modal-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={handleClose}
      >
        <motion.div
          className="modal-content photo-extension-modal"
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-header">
            <h3>
              {extensionType === 'extension' ? 'Extend Photo Upload' : 'Upgrade Photo Upload Tier'}
            </h3>
            <button className="close-btn" onClick={handleClose}>
              <FiX />
            </button>
          </div>

          {step === 'details' && (
            <div className="modal-body">
              <div className="extension-summary">
                <h4>
                  {extensionType === 'extension'
                    ? `Extend ${photoUploadStatus?.tierDisplayName || 'Current Tier'}`
                    : `Upgrade to ${tierDetails?.display_name || 'Higher Tier'}`}
                </h4>
                <div className="summary-details">
                  <div className="detail-row">
                    <span className="label">Photo Limit:</span>
                    <span className="value">{tierDetails?.photo_limit?.toLocaleString('en-IN')} photos</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Validity:</span>
                    <span className="value">1 Year from {extensionType === 'extension' ? 'now' : 'upgrade date'}</span>
                  </div>
                  {photoUploadStatus?.expiresAt && extensionType === 'extension' && (
                    <div className="detail-row">
                      <span className="label">Current Expiry:</span>
                      <span className="value">{formatPhotoDate(photoUploadStatus.expiresAt)}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="pricing-summary">
                <div className="price-row">
                  <span>Base Price:</span>
                  <span>₹{tierDetails?.extension_price}</span>
                </div>
                <div className="price-row">
                  <span>GST (18%):</span>
                  <span>₹{gstCalc?.gst.toFixed(2)}</span>
                </div>
                <div className="price-row total">
                  <span>Total Amount:</span>
                  <span>{formatCurrency(gstCalc?.total || 0)}</span>
                </div>
              </div>

              <div className="form-section">
                <h4>Billing Details</h4>
                <div className="form-group">
                  <label>Full Name *</label>
                  <input
                    type="text"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    placeholder="Enter your full name"
                  />
                </div>

                <div className="form-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    value={customerEmail}
                    onChange={(e) => setCustomerEmail(e.target.value)}
                    placeholder="Enter your email"
                  />
                </div>

                <div className="form-group">
                  <label>Phone Number *</label>
                  <input
                    type="tel"
                    value={customerPhone}
                    onChange={(e) => setCustomerPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                    placeholder="10-digit mobile number"
                    maxLength="10"
                  />
                </div>

                <div className="form-group">
                  <label>State *</label>
                  <select
                    value={customerState}
                    onChange={(e) => setCustomerState(e.target.value)}
                  >
                    <option value="">Select your state</option>
                    {indianStates.map(state => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </select>
                </div>
              </div>

              {error && <div className="error-message">{error}</div>}

              <div className="modal-footer">
                <button className="btn-cancel" onClick={handleClose}>
                  Cancel
                </button>
                <button
                  className="btn-primary"
                  onClick={handlePayment}
                  disabled={processing}
                >
                  {processing ? 'Processing...' : `Pay ${formatCurrency(gstCalc?.total || 0)}`}
                </button>
              </div>
            </div>
          )}

          {step === 'processing' && (
            <div className="modal-processing">
              <div className="spinner-large"></div>
              <h3>Processing Payment...</h3>
              <p>Please complete the payment in the Cashfree checkout.</p>
            </div>
          )}

          {step === 'success' && (
            <div className="modal-success">
              <div className="success-icon">
                <FiCheck size={48} />
              </div>
              <h3>
                {extensionType === 'extension'
                  ? 'Extension Successful!'
                  : 'Upgrade Successful!'}
              </h3>
              <p>
                Your photo upload tier has been {extensionType === 'extension' ? 'extended' : 'upgraded'} successfully.
              </p>

              {paymentResult && (
                <div className="success-details">
                  <div className="detail-row">
                    <FiCamera />
                    <span>Photo Limit: {paymentResult.photoLimit?.toLocaleString('en-IN')} photos</span>
                  </div>
                  <div className="detail-row">
                    <FiClock />
                    <span>Valid Until: {formatPhotoDate(paymentResult.newExpiryDate)}</span>
                  </div>
                  {paymentResult.invoiceNumber && (
                    <div className="detail-row">
                      <FiFileText />
                      <span>Invoice: {paymentResult.invoiceNumber}</span>
                    </div>
                  )}
                </div>
              )}

              <div className="modal-footer">
                <button
                  className="btn-primary"
                  onClick={() => {
                    handleClose();
                    window.location.reload();
                  }}
                >
                  Done
                </button>
              </div>
            </div>
          )}

          {step === 'error' && (
            <div className="modal-error">
              <div className="error-icon">
                <FiAlertCircle size={48} />
              </div>
              <h3>Payment Failed</h3>
              <p>{error || 'An error occurred during payment'}</p>

              <div className="modal-footer">
                <button className="btn-cancel" onClick={handleClose}>
                  Close
                </button>
                <button
                  className="btn-primary"
                  onClick={() => {
                    setStep('details');
                    setError('');
                  }}
                >
                  Try Again
                </button>
              </div>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

const UpgradeTiers = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();

  const eventId = searchParams.get('eventId');
  const eventName = searchParams.get('eventName');

  const [selectedTab, setSelectedTab] = useState('photo');
  const [loading, setLoading] = useState(true);
  const [hostTiers, setHostTiers] = useState([]);
  const [photoTiers, setPhotoTiers] = useState([]);
  const [currentHostTier, setCurrentHostTier] = useState(null);
  const [currentPhotoTier, setCurrentPhotoTier] = useState(null);

  // Payment modal state
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);

  // Album extension state
  const [albums, setAlbums] = useState([]);
  const [albumPricingTiers, setAlbumPricingTiers] = useState([]);
  const [loadingAlbums, setLoadingAlbums] = useState(false);
  const [showAlbumModal, setShowAlbumModal] = useState(false);
  const [selectedAlbum, setSelectedAlbum] = useState(null);

  // Photo upload extension state
  const [photoUploadStatus, setPhotoUploadStatus] = useState(null);
  const [photoExtensionPricing, setPhotoExtensionPricing] = useState([]);
  const [loadingPhotoStatus, setLoadingPhotoStatus] = useState(false);
  const [showPhotoExtensionModal, setShowPhotoExtensionModal] = useState(false);
  const [photoExtensionType, setPhotoExtensionType] = useState(null); // 'extension' or 'upgrade'
  const [selectedPhotoTier, setSelectedPhotoTier] = useState(null);

  useEffect(() => {
    if (eventId) {
      loadTiers();
      loadCurrentTiers();
    }
  }, [eventId]);

  // Load albums when album tab is selected
  useEffect(() => {
    if (selectedTab === 'albums' && eventId) {
      loadAlbums();
      loadAlbumPricing();
    }
  }, [selectedTab, eventId]);

  // Load photo upload status when photo tab is selected
  useEffect(() => {
    if (selectedTab === 'photo' && eventId) {
      loadPhotoUploadStatus();
      loadPhotoExtensionPricing();
    }
  }, [selectedTab, eventId]);

  const loadCurrentTiers = async () => {
    try {
      const { data: event, error } = await supabase
        .from('events')
        .select('host_pricing_tier_id, photo_pricing_tier_id')
        .eq('id', eventId)
        .single();
      
      if (error) throw error;
      
      setCurrentHostTier(event.host_pricing_tier_id);
      setCurrentPhotoTier(event.photo_pricing_tier_id);
    } catch (error) {
      console.error('Error loading current tiers:', error);
    }
  };

  const loadTiers = async () => {
    try {
      setLoading(true);
      
      // Load host tiers
      const { data: hostData, error: hostError } = await supabase
        .from('event_pricing_tiers')
        .select('*')
        .eq('tier_type', 'host')
        .eq('is_active', true)
        .order('sort_order', { ascending: true });
      
      if (hostError) throw hostError;
      setHostTiers(hostData || []);
      
      // Load photo tiers
      const { data: photoData, error: photoError } = await supabase
        .from('event_pricing_tiers')
        .select('*')
        .eq('tier_type', 'photo')
        .eq('is_active', true)
        .order('sort_order', { ascending: true });
      
      if (photoError) throw photoError;
      setPhotoTiers(photoData || []);
      
    } catch (error) {
      console.error('Error loading tiers:', error);
      alert('Failed to load pricing tiers');
    } finally {
      setLoading(false);
    }
  };

  const loadAlbums = async () => {
    try {
      setLoadingAlbums(true);
      const result = await getEventLinkedAlbums(eventId);
      if (result.success) {
        setAlbums(result.albums);
      } else {
        console.error('Error loading albums:', result.error);
      }
    } catch (error) {
      console.error('Error loading albums:', error);
    } finally {
      setLoadingAlbums(false);
    }
  };

  const loadAlbumPricing = async () => {
    try {
      const result = await getAlbumExtensionPricing();
      if (result.success) {
        setAlbumPricingTiers(result.pricing);
      }
    } catch (error) {
      console.error('Error loading album pricing:', error);
    }
  };

  // Load photo upload status and expiry info
  const loadPhotoUploadStatus = async () => {
    try {
      setLoadingPhotoStatus(true);

      // Get financial year from event
      const { data: event } = await supabase
        .from('events')
        .select('financial_year')
        .eq('id', eventId)
        .single();

      if (event) {
        const result = await getEventPhotoUploadStatus(eventId, event.financial_year);
        if (result.success) {
          setPhotoUploadStatus(result.status);
        } else {
          console.error('Error loading photo upload status:', result.error);
        }
      }
    } catch (error) {
      console.error('Error loading photo upload status:', error);
    } finally {
      setLoadingPhotoStatus(false);
    }
  };

  const loadPhotoExtensionPricing = async () => {
    try {
      const result = await getPhotoUploadExtensionPricing();
      if (result.success) {
        setPhotoExtensionPricing(result.pricing);
      }
    } catch (error) {
      console.error('Error loading photo extension pricing:', error);
    }
  };

  const handleUpgrade = (tier) => {
    setSelectedTier(tier);
    setShowPaymentModal(true);
  };

  const handlePaymentSuccess = (result) => {
    console.log('Payment successful:', result);
    // Refresh current tiers
    loadCurrentTiers();
  };

  const handleClosePaymentModal = () => {
    setShowPaymentModal(false);
    setSelectedTier(null);
  };

  // Album extension handlers
  const handleExtendAlbum = (album) => {
    setSelectedAlbum(album);
    setShowAlbumModal(true);
  };

  const handleCloseAlbumModal = () => {
    setShowAlbumModal(false);
    setSelectedAlbum(null);
  };

  const handleAlbumPaymentSuccess = (result) => {
    console.log('Album extension successful:', result);
    // Refresh albums
    loadAlbums();
  };

  // Photo upload extension/upgrade handlers
  const handleExtendPhotoUpload = () => {
    setPhotoExtensionType('extension');
    setSelectedPhotoTier(photoUploadStatus?.currentTierId || null);
    setShowPhotoExtensionModal(true);
  };

  const handleUpgradePhotoUpload = (tierId) => {
    setPhotoExtensionType('upgrade');
    setSelectedPhotoTier(tierId);
    setShowPhotoExtensionModal(true);
  };

  const handleClosePhotoExtensionModal = () => {
    setShowPhotoExtensionModal(false);
    setPhotoExtensionType(null);
    setSelectedPhotoTier(null);
  };

  const handlePhotoExtensionSuccess = (result) => {
    console.log('Photo upload extension/upgrade successful:', result);
    // Refresh photo upload status
    loadPhotoUploadStatus();
    loadCurrentTiers();
  };

  const currentTiers = selectedTab === 'host' ? hostTiers : photoTiers;
  const currentTierId = selectedTab === 'host' ? currentHostTier : currentPhotoTier;

  if (!eventId) {
    return (
      <DashboardLayout>
        <div className="upgrade-tiers-page">
          <div className="error-state">
            <h3>No event selected</h3>
            <p>Please select an event to upgrade</p>
            <button 
              className="btn btn-primary"
              onClick={() => navigate('/upgrade')}
            >
              Go Back
            </button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="upgrade-tiers-page">
        <div className="tiers-container">
          {/* Header */}
          <motion.div 
            className="tiers-header"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <button 
              className="back-btn"
              onClick={() => navigate('/upgrade')}
            >
              <FiArrowLeft />
            </button>
            
            <div className="header-content">
              <h1>Upgrade Invitation</h1>
              <p className="event-name">{eventName}</p>
            </div>
          </motion.div>

          {/* Tab Navigation */}
          <motion.div 
            className="tabs-container"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <button
              className={`tab ${selectedTab === 'host' ? 'active' : ''}`}
              onClick={() => setSelectedTab('host')}
            >
              Host Features
            </button>
            <button
              className={`tab ${selectedTab === 'photo' ? 'active' : ''}`}
              onClick={() => setSelectedTab('photo')}
            >
              Photo Upload
            </button>
            <button
              className={`tab ${selectedTab === 'albums' ? 'active' : ''}`}
              onClick={() => setSelectedTab('albums')}
            >
              Photographer Albums
            </button>
          </motion.div>

          {/* Section Info */}
          <motion.div
            className="section-info"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <h2>
              {selectedTab === 'host'
                ? 'Host Plans'
                : selectedTab === 'photo'
                  ? 'Photo Upload Plans'
                  : 'Photographer Albums'}
            </h2>
            <p>
              {selectedTab === 'host'
                ? 'Unlock powerful features to manage your event'
                : selectedTab === 'photo'
                  ? 'Share more memories with higher photo limits'
                  : 'Extend storage for albums linked to this event'}
            </p>
          </motion.div>

          {/* Content Grid */}
          <div className="tiers-content">
            {selectedTab === 'albums' ? (
              // Albums Tab Content
              loadingAlbums ? (
                <div className="loading-container">
                  <div className="spinner"></div>
                  <p>Loading albums...</p>
                </div>
              ) : albums.length === 0 ? (
                <div className="empty-state">
                  <FiCamera size={48} />
                  <h3>No Albums Found</h3>
                  <p>No photographer albums are linked to this event yet.</p>
                </div>
              ) : (
                <motion.div
                  className="albums-grid"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3 }}
                >
                  {albums.map((album, index) => (
                    <motion.div
                      key={album.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <AlbumCard
                        album={album}
                        onExtend={handleExtendAlbum}
                      />
                    </motion.div>
                  ))}
                </motion.div>
              )
            ) : selectedTab === 'photo' ? (
              // Photo Tab Content
              loading ? (
                <div className="loading-container">
                  <div className="spinner"></div>
                  <p>Loading pricing tiers...</p>
                </div>
              ) : (
                <>
                  {/* Show tier status card if user has paid tier and photos uploaded */}
                  {photoUploadStatus && currentPhotoTier && currentPhotoTier !== 'photo_free' && photoUploadStatus.photosCount > 0 && (
                    <div className="photo-status-container">
                      {/* Current Tier Status Card */}
                      <motion.div
                        className="photo-status-card"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                      >
                        <div className="status-header">
                          <div className="tier-info">
                            <h3>{photoUploadStatus.tierDisplayName}</h3>
                            <p>{photoUploadStatus.photoLimit?.toLocaleString('en-IN')} Photos</p>
                          </div>
                          {photoUploadStatus.expiresAt && (
                            <div className={`expiry-badge ${getExpiryStatusBadge(photoUploadStatus.daysUntilExpiry, photoUploadStatus.isExpired).variant}`}>
                              <FiClock />
                              <span>{getExpiryStatusBadge(photoUploadStatus.daysUntilExpiry, photoUploadStatus.isExpired).label}</span>
                            </div>
                          )}
                        </div>

                        <div className="status-details">
                          <div className="detail-row">
                            <span className="label">Photos Uploaded:</span>
                            <span className="value">{photoUploadStatus.photosCount || 0} / {photoUploadStatus.photoLimit?.toLocaleString('en-IN')}</span>
                          </div>
                          {photoUploadStatus.expiresAt && (
                            <div className="detail-row">
                              <span className="label">Expires On:</span>
                              <span className="value">{formatPhotoDate(photoUploadStatus.expiresAt)}</span>
                            </div>
                          )}
                          {photoUploadStatus.activatedAt && (
                            <div className="detail-row">
                              <span className="label">Activated On:</span>
                              <span className="value">{formatPhotoDate(photoUploadStatus.activatedAt)}</span>
                            </div>
                          )}
                        </div>

                        <div className="status-actions">
                          {photoUploadStatus.canExtend && (
                            <button
                              className="btn btn-primary"
                              onClick={handleExtendPhotoUpload}
                            >
                              <FiClock />
                              Extend for 1 Year (₹{photoUploadStatus.extensionPrice})
                            </button>
                          )}
                          {photoUploadStatus.canUpgrade && photoUploadStatus.nextTierId && (
                            <button
                              className="btn btn-secondary"
                              onClick={() => handleUpgradePhotoUpload(photoUploadStatus.nextTierId)}
                            >
                              Upgrade to {photoUploadStatus.nextTierDisplayName} (₹{photoUploadStatus.nextTierPrice})
                            </button>
                          )}
                        </div>
                      </motion.div>
                    </div>
                  )}

                  {/* Always show tier cards for initial purchase or upgrades */}
                  <motion.div
                    className="tiers-grid"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.3 }}
                  >
                    {currentTiers.map((tier, index) => (
                      <motion.div
                        key={tier.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                      >
                        <TierCard
                          tier={tier}
                          isCurrent={currentTierId === tier.id}
                          tierType={selectedTab}
                          onUpgrade={handleUpgrade}
                        />
                      </motion.div>
                    ))}
                  </motion.div>

                  {/* Extension Section - Always visible */}
                  <motion.div
                    className="extension-section"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                  >
                    <div className="extension-header">
                      <FiClock size={24} />
                      <div>
                        <h3>Photo Upload Extension</h3>
                        <p>Extend your photo upload tier validity by 1 year</p>
                      </div>
                    </div>

                    {currentPhotoTier === 'photo_free' || (photoUploadStatus?.photosCount || 0) <= 100 ? (
                      <div className="extension-disabled">
                        <p className="disabled-message">
                          {currentPhotoTier === 'photo_free'
                            ? 'Upgrade to a paid tier to enable extensions'
                            : 'Upload more than 100 photos to enable extensions'}
                        </p>
                        <button className="btn-extension" disabled>
                          Extension Available After Upgrade
                        </button>
                      </div>
                    ) : (
                      <div className="extension-enabled">
                        <p className="enabled-message">
                          Your {photoUploadStatus?.tierDisplayName} tier can be extended
                        </p>
                        <button
                          className="btn-extension"
                          onClick={handleExtendPhotoUpload}
                        >
                          <FiClock />
                          Extend for 1 Year (₹{photoUploadStatus?.extensionPrice})
                        </button>
                      </div>
                    )}
                  </motion.div>
                </>
              )
            ) : (
              // Host Tiers - Show Normal Tier Cards
              loading ? (
                <div className="loading-container">
                  <div className="spinner"></div>
                  <p>Loading pricing tiers...</p>
                </div>
              ) : (
                <motion.div
                  className="tiers-grid"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3 }}
                >
                  {currentTiers.map((tier, index) => (
                    <motion.div
                      key={tier.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <TierCard
                        tier={tier}
                        isCurrent={currentTierId === tier.id}
                        tierType={selectedTab}
                        onUpgrade={handleUpgrade}
                      />
                    </motion.div>
                  ))}
                </motion.div>
              )
            )}
          </div>

          {/* Info Footer */}
          <motion.div
            className="info-footer"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            <div className="info-icon">
              <FiInfo />
            </div>
            <div className="info-text">
              <p>All prices are in Indian Rupees (₹) and subject to 18% GST.</p>
              <p>Upgrades are event-specific and will apply only to the selected invitation.</p>
            </div>
          </motion.div>
        </div>

        {/* Payment Modal */}
        <PaymentModal
          isOpen={showPaymentModal}
          onClose={handleClosePaymentModal}
          tier={selectedTier}
          tierType={selectedTab}
          eventId={eventId}
          eventName={eventName}
          user={user}
          onPaymentSuccess={handlePaymentSuccess}
        />

        {/* Album Extension Modal */}
        <AlbumExtensionModal
          isOpen={showAlbumModal}
          onClose={handleCloseAlbumModal}
          album={selectedAlbum}
          eventId={eventId}
          eventName={eventName}
          user={user}
          pricingTiers={albumPricingTiers}
          onPaymentSuccess={handleAlbumPaymentSuccess}
        />

        {/* Photo Upload Extension/Upgrade Modal */}
        <PhotoExtensionModal
          isOpen={showPhotoExtensionModal}
          onClose={handleClosePhotoExtensionModal}
          eventId={eventId}
          eventName={eventName}
          user={user}
          extensionType={photoExtensionType}
          selectedTierId={selectedPhotoTier}
          photoUploadStatus={photoUploadStatus}
          photoExtensionPricing={photoExtensionPricing}
          onPaymentSuccess={handlePhotoExtensionSuccess}
        />
      </div>
    </DashboardLayout>
  );
};

export default UpgradeTiers;